import { useReactiveVar } from '@apollo/client';
import { addDays, formatISO as formatISO8601 } from 'date-fns';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import DashboardHeader from '../DashboardHeader';
import ShiftGrid from './ShiftGrid';
import ShiftDate from './ShiftGrid/ShiftsDate';
import ShiftsColumn from './ShiftGrid/ShiftsColumn';
import './index.css';

import AccountStateError from '@/components/AccountStateError';
import Card from '@/components/Card';
import NeedsApprovalAlert from '@/components/NeedsApprovalAlert';
import Stack from '@/components/Stack';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import { TIME_TO_REFRESH } from '@/constants/general';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { useListShiftsByWeekQuery, useNeedsApprovalQuery } from '@/graphql';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import useAuth from '@/hooks/useAuth';
import useMediaQuery from '@/hooks/useMediaQuery';
import { JobsAPI } from '@/services/jobsAPI';
import { selectDashboardFilters } from '@/store/slices/filtersSlice';
import {
  selectRefetchDashboard,
  setRefetchDashboardData,
} from '@/store/slices/layoutSlice';
import {
  CurrentCustomerAdminFragment,
  JobFilterSetInput,
  JobStatusEnum,
} from '@/types/graphql';
import {
  getAccountErrorDesc,
  getAccountErrorTitle,
  hasValidCustomerAccountStatus,
} from '@/util/accountstatus';
import {
  currentAdminVar,
  currentAgencyVar,
  currentTenantVar,
} from '@/util/apollo/cache';
import useAnalytics from '@/util/analytics';
import { GAEvent } from '@/constants/gaevents';
import { getInitialStartDate, updateUrlParams } from '@/util/dashboard';
import ls, { ACCOUNT_DISABLED } from '@/util/localstorage';

const ShiftWeekList = () => {
  const [loadShiftData, setLoadShiftData] = useState(false);
  const [showSelectedDate, setShowSelectedDate] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [showNeedsApproval, setShowNeedsApproval] = useState(true);
  const currentTenant = useReactiveVar(currentTenantVar);
  const { logEvent } = useAnalytics();

  const isFeatFlagDisabledCustomerLoginAllowedEnabled = useFeatureValue(
    'disabled-customer-login',
    false,
  );
  const needsApprovalStatus = useFeatureValue(
    FEATURE_TOGGLE.DashboardApprovalStatusFeature,
    false,
  );

  const currentAdminUser = useReactiveVar(
    currentAdminVar,
  ) as CurrentCustomerAdminFragment;
  const accountDisabled = ls.get(ACCOUNT_DISABLED) === 'true';
  const dispatch = useAppDispatch();
  const refetchData = useAppSelector(selectRefetchDashboard);

  const hasValidAccountStatus = () =>
    hasValidCustomerAccountStatus(
      isFeatFlagDisabledCustomerLoginAllowedEnabled,
      currentAdminUser,
      accountDisabled,
    );

  const currentAgency = useReactiveVar(currentAgencyVar);
  const { currentAdmin, isCurrentCustomerAdmin } = useAuth();

  const [startDate, setStartDate] = useState(() =>
    getInitialStartDate(logEvent, currentAgency!.id),
  );

  const goBack = useCallback(
    debounce(() => setStartDate((date) => addDays(date, -7)), 200),
    [],
  );
  const goForward = useCallback(
    debounce(() => setStartDate((date) => addDays(date, 7)), 200),
    [],
  );

  const startDateISO = useMemo(
    () => formatISO8601(startDate, { representation: 'date' }),
    [startDate],
  );

  useEffect(() => {
    updateUrlParams({ sd: startDateISO });
  }, [startDateISO]);

  useEffect(() => {
    setStartDate(getInitialStartDate(logEvent, currentAgency!.id));
  }, [location.search]);

  const dashboardFilters = useAppSelector(selectDashboardFilters) || {};

  /** Build the GraphQL filter from Redux state, forcing customer-admin lock. */
  const finalFilters = useMemo<JobFilterSetInput>(() => {
    const value: JobFilterSetInput = {
      ...(dashboardFilters as JobFilterSetInput),
    };
    if (isCurrentCustomerAdmin(currentAdmin)) {
      value.customers = {
        value: [(currentAdmin as CurrentCustomerAdminFragment).customer.id],
      };
    }
    return value;
  }, [dashboardFilters, currentAdmin]);

  const { data, loading, refetch } = useListShiftsByWeekQuery({
    variables: {
      agencyId: currentAgency!.id,
      startDate: startDateISO,
      filters: finalFilters,
      timeZone: Intl?.DateTimeFormat()?.resolvedOptions().timeZone,
    },
    pollInterval: TIME_TO_REFRESH,
  });

  const { data: needsApprovalData } = useNeedsApprovalQuery({
    variables: {
      agencyId: currentAgency!.id,
      startDate: formatISO8601(addDays(startDate, -7), {
        representation: 'date',
      }),
      endDate: formatISO8601(addDays(startDate, -1), {
        representation: 'date',
      }),
      tenantId: currentTenant!.id,
      filters: finalFilters,
    },
  });

  useEffect(() => {
    if (refetchData) {
      refetch?.();
      dispatch(setRefetchDashboardData(false));
    }
  }, [refetchData, refetch, dispatch]);

  useEffect(() => {
    setShowNeedsApproval(true);
    if (!loading) setShowSelectedDate(true);
  }, [data, loading]);

  const [qualityGateActiveJobIds, setQualityGateActiveJobIds] = useState<
    Set<number>
  >(new Set());
  const [qualityGateSupplyInfo, setQualityGateSupplyInfo] = useState<
    Record<number, { tier_applied: string; low_supply: boolean }>
  >({});

  useEffect(() => {
    const shiftsByWeek = data?.agency?.shiftsByWeek;
    if (
      isCurrentCustomerAdmin(currentAdmin) ||
      !shiftsByWeek ||
      shiftsByWeek.length === 0
    ) {
      setQualityGateActiveJobIds(new Set());
      return;
    }

    const jobIds = Array.from(
      new Set(
        shiftsByWeek
          .map((s) => Number(s.job.id))
          .filter((id) => Number.isFinite(id) && id > 0),
      ),
    );

    let cancelled = false;
    JobsAPI.getQualityGateActiveJobIds(jobIds)
      .then(({ activeJobIds, supplyInfo }) => {
        if (!cancelled) {
          setQualityGateActiveJobIds(activeJobIds);
          setQualityGateSupplyInfo(supplyInfo);
        }
      })
      .catch((error) => {
        console.error('[Dashboard] Quality gate fetch error:', error);
        if (!cancelled) {
          setQualityGateActiveJobIds(new Set());
          setQualityGateSupplyInfo({});
        }
      });

    return () => {
      cancelled = true;
    };
  }, [data, currentAdmin]);

  const dashboardJobFilters = useAppSelector(
    (state) => state.filters.dashboardFilters?.dashboardJobStatus,
  );
  const hasCancelledFilterApplied = () =>
    (dashboardJobFilters || []).includes(JobStatusEnum.CANCELLED);

  const getShiftData = () => {
    if (!data) return [];
    if (!hasCancelledFilterApplied()) {
      return data.agency.shiftsByWeek.filter(
        (s) => s.job.cancelledAt === null || s.job.cancelledAt === undefined,
      );
    }
    return data.agency.shiftsByWeek;
  };

  const phoneOnly = useMediaQuery('(max-width: 559px)');

  const logNonNavigationEvent = () => {
    logEvent(GAEvent.DashboardNonCurrentWeekNavigation, currentAgency!.id, {});
  };

  return (
    <Card noRadius={phoneOnly}>
      <Card.Section>
        <Stack vertical>
          {isCurrentCustomerAdmin(currentAdmin) &&
            needsApprovalStatus &&
            (needsApprovalData?.needsApproval.length || 0) > 0 &&
            showNeedsApproval && (
              <NeedsApprovalAlert
                jobsCount={needsApprovalData?.needsApproval.length || 0}
                viewLastWeek={() => {
                  logEvent(GAEvent.ViewLastWeekPressed, currentAgency!.id, {});
                  goBack();
                }}
                dismiss={() => {
                  logEvent(GAEvent.DismissNeedsApproval, currentAgency!.id, {});
                  setShowNeedsApproval(false);
                }}
                phoneOnly={phoneOnly}
              />
            )}

          <DashboardHeader
            startDate={startDate}
            onPrevWeek={() => {
              goBack();
              logNonNavigationEvent();
            }}
            onNextWeek={() => {
              goForward();
              logNonNavigationEvent();
            }}
          />

          {phoneOnly && showSelectedDate && (
            <ShiftDate
              startDate={startDate}
              shifts={getShiftData()}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              setSelectedShifts={setSelectedShifts}
              loading={loading}
              loadData={loadShiftData}
              setLoadData={setLoadShiftData}
            />
          )}
        </Stack>
        {!hasValidAccountStatus() && (
          <AccountStateError
            message={getAccountErrorDesc(accountDisabled, currentAdminUser)}
            title={getAccountErrorTitle(accountDisabled, currentAdminUser)}
          />
        )}
      </Card.Section>

      {hasValidAccountStatus() && !phoneOnly && (
        <ShiftGrid
          loading={loading}
          shifts={getShiftData()}
          startDate={startDate}
          qualityGateActiveJobIds={qualityGateActiveJobIds}
          qualityGateSupplyInfo={qualityGateSupplyInfo}
        />
      )}
      {phoneOnly && (
        <ShiftsColumn
          date={new Date()}
          shifts={selectedShifts}
          qualityGateActiveJobIds={qualityGateActiveJobIds}
          qualityGateSupplyInfo={qualityGateSupplyInfo}
        />
      )}
    </Card>
  );
};

export default ShiftWeekList;
